// src/pages/DealExplorerAi.jsx
import React, { useState, useEffect, useRef } from 'react';
import { OpenAI } from 'openai';
import axios from 'axios';
import dayjs from 'dayjs';
import ListingItem from '../components/ListingItem.jsx';
import OfferModal from './OfferModal.jsx';
import elevateImg from '../assets/elevateai.png';
import CircularProgress from '@mui/material/CircularProgress';

// ── ENV ───────────────────────────────────────────────────────────────────────
const RAPIDAPI_HOST = process.env.REACT_APP_RAPIDAPI_HOST;
const RAPIDAPI_KEY  = process.env.REACT_APP_RAPIDAPI_KEY;
const OPENAI_KEY    = process.env.REACT_APP_OPENAI_KEY;

// ── OpenAI client (used for param extraction) ────────────────────────────────
const openai = new OpenAI({
  apiKey: OPENAI_KEY,
  dangerouslyAllowBrowser: true
});

// — the only tags we’re allowed to pass
const allowedTags = [
  'basement','fireplace','central_air','central_heat','forced_air','den_or_office','spa_or_hot_tub','dining_room',
  'swimming_pool','disability_features','family_room','laundry_room','hardwood_floors','rv_or_boat_parking','tennis_court',
  'game_room','horse_facilities','lease_option','energy_efficient_home','garage_1_or_more','garage_2_or_more','garage_3_or_more',
  'carport','single_story','two_or_more_stories','recreation_facilities','community_swimming_pool','community_clubhouse',
  'community_tennis_court','community_golf','community_spa_or_hot_tub','community_security_features','community_boat_facilities',
  'community_horse_facilities','community_park','senior_community','corner_lot','waterfront','cul_de_sac','water_view',
  'view','hill_or_mountain_view','lake_view','river_view','golf_course_view','city_view','ocean_view','golf_course_lot_or_frontage',
  'washer_dryer','community_doorman','pets_allowed','elevator','community_elevator','community_outdoor_space',
  'furniture','furnished','dishwasher','community_no_fee','community_gym','recreation_facility'
];

// — Extract structured params via GPT
async function extractSearchParams(input) {
  const system = `
You are given a user’s free-form real-estate search text. Your goal is to extract *only* valid Realtor16 API parameters into a flat JSON object, using these exact keys:

  location, page, sort,
  type,
  beds-min, beds-max,
  baths-min, baths-max,
  limit, search_radius,
  sqft-min, sqft-max,
  lot_sqft-min, lot_sqft-max,
  new_construction,
  hoa_fee-min, hoa_fee-max,
  foreclosure,
  has_tour, has_3d_tour,
  open_house-min, open_house-max,
  list_price-min, list_price-max,
  price_reduced_date-min,
  pending,
  tags,
  keywords

Exact "type" values only: single_family, condos, townhomes, duplex_triplex, multi_family, mobile, farm, land.
Map synonyms to exact: e.g. "townhouse" → "townhomes", "single family" → "single_family".

Tags must be one or more from: ${allowedTags.join(', ')}.

- Numeric fields should be numbers; Boolean fields booleans; date fields YYYY-MM-DD.
- Only output keys that were mentioned.
- Return *only* valid JSON.
`.trim();

  const resp = await openai.chat.completions.create({
    model: 'gpt-3.5-turbo',
    temperature: 0,
    messages: [
      { role: 'system', content: system },
      { role: 'user',   content: input }
    ]
  });

  try {
    return JSON.parse(resp.choices[0].message.content);
  } catch {
    return {};
  }
}

// — pull out a numeric cashflow threshold if mentioned
function extractCashflowThreshold(query) {
  const m = query.toLowerCase().match(/cashflow(?:ing)?[^$0-9]{0,10}(\d{2,5})/);
  return m ? parseInt(m[1], 10) : null;
}

// ── Simple pulse CSS for mic button ───────────────────────────────────────────
const pulseStyle = `
@keyframes pulse {
  0%   { transform: scale(1);   box-shadow: 0 0 0 0 rgba(0, 128, 0, 0.3); }
  70%  { transform: scale(1.06);box-shadow: 0 0 0 12px rgba(0, 128, 0, 0); }
  100% { transform: scale(1);   box-shadow: 0 0 0 0 rgba(0, 128, 0, 0); }
}
.elevate-mic.pulsing { animation: pulse 1s ease-in-out infinite; }
`;

// ── Component ─────────────────────────────────────────────────────────────────
export default function DealExplorerAi() {
  const [query, setQuery]       = useState('');
  const [listings, setListings] = useState([]);
  const [loading, setLoading]   = useState(false);
  const [selected, setSelected] = useState(null);

  // typing placeholder
  const prompts = [
    'Show me homes that accept Section 8 in Memphis under $300k with at least 3 bedrooms',
    'Find Section 8–approved single-family houses in Chicago under $200k with central air',
    'Show me Section 8–qualifying condos in Orlando under $150k near a community pool',
    'List Section 8 eligible duplex properties in Cleveland under $180k with a detached garage',
    'Search for Section 8–approved multi-family buildings in Houston under $350k with disability features',
    'Find 3-bedroom corner-lot homes in Hudson, Florida under $1M',
    'Show me waterfront cottages in Portland, Maine under $500k',
    'Search for farm land parcels in Kansas under $200k with a river view',
    'Find two-story single-family homes in Denver under $450k with hardwood floors',
    'Show me condos in Miami with a pool under $300k',
    'List townhomes in Sacramento under $350k near the light rail',
    'Search for mobile homes in Tucson under $120k with RV parking',
    'Find properties with a tennis court in Scottsdale under $600k',
    'Show me family homes in Raleigh under $400k with a den or office',
    'Search for energy-efficient homes in Seattle under $550k',
    'Find homes with a fireplace in Boston under $700k',
    'Show me lake-view cabins in Minnesota under $350k',
    'Search for open-house listings in Austin this weekend under $500k',
    'Find new-construction single-family homes in Phoenix under $450k',
    'Show me properties in Tuscaloosa that cashflow at least $500 a month'
  ];
  const [phIndex, setPhIndex]     = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [displayText, setDisplayText] = useState('');
  const [isDeleting, setIsDeleting]   = useState(false);

  // 🎤 voice state (web)
  const [recording, setRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef        = useRef([]);

  // typing effect
  useEffect(() => {
    const current = prompts[phIndex];
    let timeout;
    if (!isDeleting) {
      if (charIndex < current.length) {
        timeout = setTimeout(() => {
          setDisplayText(current.slice(0, charIndex + 1));
          setCharIndex(ci => ci + 1);
        }, 20);
      } else {
        timeout = setTimeout(() => setIsDeleting(true), 1500);
      }
    } else {
      if (charIndex > 0) {
        timeout = setTimeout(() => {
          setDisplayText(current.slice(0, charIndex - 1));
          setCharIndex(ci => ci - 1);
        }, 20);
      } else {
        timeout = setTimeout(() => {
          setIsDeleting(false);
          setPhIndex(pi => (pi + 1) % prompts.length);
        }, 5);
      }
    }
    return () => clearTimeout(timeout);
  }, [charIndex, isDeleting, phIndex, prompts]);

  const hasResults   = listings.length > 0;
  const showSpinner  = loading || isProcessing;

  // ── Voice: start/stop & transcribe ─────────────────────────────────────────
  const getBestAudioMime = () => {
    if (MediaRecorder.isTypeSupported('audio/webm')) return 'audio/webm';
    if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) return 'audio/webm;codecs=opus';
    if (MediaRecorder.isTypeSupported('audio/mp4')) return 'audio/mp4';
    if (MediaRecorder.isTypeSupported('audio/aac')) return 'audio/aac';
    return '';
  };

  async function startRecording() {
    if (recording || isProcessing) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime   = getBestAudioMime();
      const rec    = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);

      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = async () => {
        try {
          const blob = new Blob(chunksRef.current, { type: rec.mimeType || 'audio/webm' });
          await transcribeAndSearch(blob);
        } finally {
          stream.getTracks().forEach(t => t.stop());
        }
      };

      mediaRecorderRef.current = rec;
      rec.start();
      setRecording(true);
    } catch (err) {
      console.error('Mic permission / recorder error:', err);
      setRecording(false);
    }
  }

  function stopRecording() {
    if (!recording) return;
    try {
      setRecording(false);
      mediaRecorderRef.current?.stop();
    } catch (e) {
      console.error('Stop recorder error:', e);
    }
  }

  // 👉 click-to-toggle handler
  function toggleRecording() {
    if (showSpinner) return;
    if (recording) stopRecording(); else startRecording();
  }

  async function transcribeAndSearch(blob) {
    if (!OPENAI_KEY) {
      console.error('Missing REACT_APP_OPENAI_KEY');
      return;
    }
    setIsProcessing(true);
    setLoading(true);
    try {
      const ext  = (blob.type.includes('mp4') || blob.type.includes('aac')) ? 'm4a' : 'webm';
      const file = new File([blob], `speech.${ext}`, { type: blob.type || `audio/${ext}` });

      const form = new FormData();
      form.append('file', file);
      form.append('model', 'whisper-1');

      const res  = await fetch('https://api.openai.com/v1/audio/transcriptions', {
        method: 'POST',
        headers: { Authorization: `Bearer ${OPENAI_KEY}` },
        body: form
      });

      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`Whisper error: ${res.status} ${txt}`);
      }

      const { text } = await res.json();
      setQuery(text || '');
      await handleSearch(text || '');
    } catch (e) {
      console.error(e);
    } finally {
      setIsProcessing(false);
      setLoading(false);
    }
  }

  // ── SEARCH ─────────────────────────────────────────────────────────────────
  const handleSearch = async (incoming) => {
    const qInput = (incoming ?? query).trim();
    if (!qInput) return;
    setLoading(true);

    // 1) extract params
    const params = await extractSearchParams(qInput);

    // 2) choose endpoint
    const endpoint = params.sort === 'for_rent'
      ? '/search/forrent'
      : params.sort === 'forsold'
      ? '/search/forsold'
      : '/search/forsale';

    // 3) destructure out any incoming `tags` or `keywords`
    const { tags: _t, keywords: _k, ...rest } = params;

    // 4) sanitize into only allowed tags
    const incomingTags = Array.isArray(_t) ? _t : (_t ? [_t] : []);
    const sanitized = incomingTags.filter(t => allowedTags.includes(t));

    // 5) build our final payload — drop all free-text keywords
    const q = {
      ...rest,
      search_radius: rest.search_radius ?? 5,
      limit:         rest.limit ?? 50,
      ...(sanitized.length
        ? { tags: sanitized.join(','), keywords: sanitized.join(',') }
        : {}
      )
    };

    // 6) Realtor16 call
    try {
      const resp = await axios.get(
        `https://${RAPIDAPI_HOST}${endpoint}`,
        {
          params: q,
          headers: {
            'x-rapidapi-host': RAPIDAPI_HOST,
            'x-rapidapi-key':  RAPIDAPI_KEY
          }
        }
      );
      const props = resp.data.properties || [];

      // 7) normalize + compute cashflow
      const mapped = props.map(prop => {
        const addr = prop.location?.address || {};
        const raw  = prop.primary_photo?.href || prop.photos?.[0]?.href || '';
        const img  = raw.replace(/(\.[^/.]+)$/, '-w2048_h1536$1');
        const days = dayjs().diff(dayjs(prop.list_date), 'day');
        return {
          ...prop,
          id:               prop.listing_id,
          property_id:      prop.property_id,
          price:            prop.list_price,
          bedrooms:         prop.description?.beds,
          bathrooms:        Number(prop.description?.baths_consolidated)||0,
          daysOnMarket:     days,
          formattedAddress: addr.line
            ? `${addr.line}, ${addr.city}, ${addr.state_code} ${addr.postal_code}`
            : undefined,
          streetAddress:    addr.line,
          city:             addr.city,
          state:            addr.state_code,
          zipcode:          addr.postal_code,
          propertyType:     (prop.description?.type||'').replace('_',' '),
          imgSrc:           img,
          name:             prop.description?.name||addr.line||'',
        };
      });

      const withCF = mapped.map(l => {
        const mortgage1 = Math.round(l.price * 0.0065);
        const tiers     = [0,1207,1355,1781,2086];
        const idx       = l.bedrooms > 3 ? 4 : l.bedrooms;
        const FMR       = tiers[idx] || 0;
        return { ...l, mortgage1, cashflow: FMR - mortgage1 };
      });

      // 8) client-side cashflow filter
      const wantsCF = qInput.toLowerCase().includes('cashflow');
      const cfMin   = extractCashflowThreshold(qInput) ?? 200;
      const finalL  = wantsCF
        ? withCF.filter(l => l.cashflow >= cfMin)
        : withCF;

      setListings(finalL);
    } catch (err) {
      console.error('Realtor16 error:', err);
      setListings([]);
    } finally {
      setLoading(false);
    }
  };

  // ── styles ──
  const containerStyle = {
    background: '#fff',
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column'
  };
  const searchWrapper = hasResults
    ? { display:'flex',flexDirection:'column',justifyContent:'center',alignItems:'center',padding:'16px 0' }
    : { display:'flex',flexDirection:'column',justifyContent:'center',alignItems:'center',height:'100vh' };

  // Initial (no results): input centered + BIG mic under it
  const initialStackStyle = {
    display:'flex',
    flexDirection:'column',
    alignItems:'center',
    width:'80%', maxWidth:700
  };
  const inputFullStyle = {
    width:'100%',
    padding:'12px 24px', fontSize:18,
    border:'1px solid #ccc', borderRadius:9999,
    outline:'none'
  };
  const bigMicBtn = {
    marginTop: 20, height: 120, width: 120, borderRadius: 60,
    justifyContent: 'center', alignItems: 'center',
    display:'flex',
    border:'1px solid #ccc',
    background: recording ? '#2e7d32' : '#fff',
    borderColor: recording ? '#2e7d32' : '#ccc',
    cursor:'pointer'
  };
  const bigMicIcon = { width: 40, height: 65, filter: recording ? 'invert(1)' : 'none' };

  // After results: input row + SMALL mic on right
  const inputRowStyle = {
    display:'flex',
    alignItems:'center',
    width:'80%', maxWidth:700
  };
  const inputFlexStyle = {
    flex: 1,
    padding:'12px 24px', fontSize:18,
    border:'1px solid #ccc', borderRadius:9999,
    outline:'none'
  };
  const smallMicBtn = {
    marginLeft: 12, height: 48, width: 48, borderRadius: 24,
    justifyContent: 'center', alignItems: 'center',
    display:'flex',
    border:'1px solid #ccc',
    background: recording ? '#2e7d32' : '#fff',
    borderColor: recording ? '#2e7d32' : '#ccc',
    cursor:'pointer'
  };
  const smallMicIcon = { width: 15, height: 30, filter: recording ? 'invert(1)' : 'none' };

  const gridStyle = {
    display:'grid',
    gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))',
    gap:16,padding:16,flex:1,overflowY:'auto'
  };

  return (
    <div style={containerStyle}>
      <style>{pulseStyle}</style>

      <div style={searchWrapper}>
        <img
          src={elevateImg}
          alt="Elevate AI"
          style={{ width:'350px', marginBottom:'30px' }}
        />

        {/* SINGLE place for either spinner OR the input/mic layouts */}
        {showSpinner ? (
          <div style={{ display:'flex', justifyContent:'center', alignItems:'center', height: 160 }}>
            <CircularProgress size={40} sx={{ color:'#2e7d32' }} />
          </div>
        ) : (
          <>
            {!hasResults ? (
              // Initial layout: input with BIG mic under it
              <div style={initialStackStyle}>
                <input
                  style={inputFullStyle}
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  onKeyDown={e => e.key==='Enter' && handleSearch()}
                  placeholder={displayText}
                />
                <button
                  aria-label={recording ? 'Stop recording' : 'Start recording'}
                  onClick={toggleRecording}
                  disabled={showSpinner}
                  className={`elevate-mic ${recording ? 'pulsing' : ''}`}
                  style={bigMicBtn}
                  title={recording ? 'Click to stop' : 'Click to start'}
                >
                  <img
                    src={require('../assets/microphone.png')}
                    alt="mic"
                    style={bigMicIcon}
                  />
                </button>
              </div>
            ) : (
              // Results layout: input with SMALL mic on the right
              <div style={inputRowStyle}>
                <input
                  style={inputFlexStyle}
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  onKeyDown={e => e.key==='Enter' && handleSearch()}
                  placeholder={displayText}
                />
                <button
                  aria-label={recording ? 'Stop recording' : 'Start recording'}
                  onClick={toggleRecording}
                  disabled={showSpinner}
                  className={`elevate-mic ${recording ? 'pulsing' : ''}`}
                  style={smallMicBtn}
                  title={recording ? 'Click to stop' : 'Click to start'}
                >
                  <img
                    src={require('../assets/microphone.png')}
                    alt="mic"
                    style={smallMicIcon}
                  />
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {hasResults && (
        <div style={gridStyle}>
          {listings.map(l => (
            <ListingItem
              key={l.id}
              listing={l}
              mortgage1={l.mortgage1}
              onClick={() => setSelected(l)}
            />
          ))}
        </div>
      )}

      {selected &&
        <OfferModal listing={selected} onClose={() => setSelected(null)} />
      }
    </div>
  );
}